import {
    createContext,
    useContext,
    useState,
    ReactNode,
    useEffect,
} from 'react';
import { Theme } from './types';
import { lightTheme } from './lightTheme';
import { darkTheme } from './darkTheme';
import { applyTheme } from './applyTheme';
import { ThemeProvider as StyledProvider } from 'styled-components';
import { useLazyGetProfileDetailsQuery } from '@src/services/profile/profileDetails/profileDetailsApiSlice';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentUser } from '@src/store/user/userSlice';
import { setProfile } from '@src/store/profile/profileSlice';

interface ThemeContextValue {
    theme: Theme;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
    const dispatch = useDispatch();
    const user = useSelector(selectCurrentUser);
    const [theme, setTheme] = useState<Theme>(lightTheme);

    const [getProfile, { data: profile, isLoading }] =
        useLazyGetProfileDetailsQuery({});

    useEffect(() => {
        const token = user?.accessToken;
        if (token) {
            getProfile({});
        }
    }, [user?.accessToken, getProfile]);

    const [brandData, setBrandData] = useState<{
        primaryColor: string | null;
        secondaryColor: string | null;
        logoUrl: string | null;
    } | null>(null);

    const [mode, setMode] = useState<'light' | 'dark'>(() => {
        return (
            (localStorage.getItem('themeMode') as 'light' | 'dark') || 'light'
        );
    });

    const applyBrandToTheme = (
        baseTheme: Theme,
        brand: typeof brandData
    ): Theme => ({
        ...baseTheme,
        semantic: {
            ...baseTheme.semantic,
            primary: brand?.primaryColor ?? baseTheme.semantic.primary,
            primaryHover:
                brand?.primaryColor ?? baseTheme.semantic.primaryHover,
            secondary: brand?.secondaryColor ?? baseTheme.semantic.secondary,
            secondaryHover:
                brand?.secondaryColor ?? baseTheme.semantic.secondaryHover,
        },
        logoUrl: brand?.logoUrl ?? baseTheme.logoUrl,
    });

    const toggleTheme = () => {
        setMode((prev) => {
            const newMode = prev === 'light' ? 'dark' : 'light';
            localStorage.setItem('themeMode', newMode);
            return newMode;
        });
    };

    useEffect(() => {
        const base = mode === 'light' ? lightTheme : darkTheme;
        const updated = brandData ? applyBrandToTheme(base, brandData) : base;
        setTheme({ ...updated, mode });
        applyTheme(updated);
    }, [mode, brandData]);

    /**
     * Validates whether a given URL is a valid favicon URL.
     *
     * The function attempts to construct a `URL` object using the provided string and the current page's origin as a base.
     * A URL is considered valid if it uses either the `http:` or `https:` protocol.
     *
     * @function isValidFaviconUrl
     * @param {string} url - The favicon URL to validate. Can be an absolute or relative URL.
     * @returns {boolean} `true` if the URL is valid and uses `http` or `https` protocol; otherwise, `false`.
     *
     * @example
     * isValidFaviconUrl('https://example.com/favicon.ico'); // true
     * isValidFaviconUrl('/favicon.ico'); // true (resolved relative to current origin)
     * isValidFaviconUrl('ftp://example.com/favicon.ico'); // false
     * isValidFaviconUrl('invalid-url'); // false
     */
    function isValidFaviconUrl(url: string) {
        try {
            const parsed = new URL(url, window.location.origin);

            return parsed.protocol === 'http:' || parsed.protocol === 'https:';
        } catch {
            return false;
        }
    }

    useEffect(() => {
        const logo = profile?.logo?.trim();

        if (profile) {
            dispatch(setProfile(profile));
        }

        setBrandData({
            primaryColor: profile?.primaryColor?.trim() || null,
            secondaryColor: profile?.secondaryColor?.trim() || null,
            logoUrl: logo || null,
        });

        if (logo && isValidFaviconUrl(logo)) {
            setFavicon(logo);
        }
    }, [profile]);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            <StyledProvider theme={theme}>{children}</StyledProvider>
        </ThemeContext.Provider>
    );
};

/**
 * Sets or updates the favicon of the current document.
 *
 * This function searches for an existing `<link rel="icon">` element in the document head.
 * If none is found, it creates one. It then updates the favicon by setting its `href` to the provided URL.
 *
 * @function setFavicon
 * @param {string} url - The URL of the favicon to set. Can be an absolute or relative path to an image file (e.g., `/favicon.ico`, `https://example.com/icon.png`).
 *
 * @example
 * setFavicon('/images/favicon.ico');
 * // Updates the browser tab icon to the specified image.
 *
 * @sideEffect Modifies the DOM by creating or updating a `<link rel="icon">` element in the document head.
 */
function setFavicon(url: string) {
    let link = document.querySelector(
        "link[rel~='icon']"
    ) as HTMLLinkElement | null;

    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
    }

    link.href = url;
}

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) throw new Error('useTheme must be used within ThemeProvider');
    return context;
};
